import { Controller } from "@hotwired/stimulus"

// End-of-month recap deck (reports/week_review).
//
// One slide at a time, advanced by a tap on the right half, a swipe, or the
// CTA. Nothing here scrolls: each slide is a whole screen, so the deck owns
// the viewport and the gesture.
//
// Everything visual is driven by two class flips — `.is-live` on the slide and
// `--tint` on the host — so a slide's entry choreography is declared once in
// CSS and simply replayed when the slide becomes live. The only motion this
// controller computes itself is the stat count-up, which cannot be expressed
// as a keyframe because the value is different on every slide.
export default class extends Controller {
  // No rail target: each card renders its own progress rail already in the
  // right state, so the position cannot drift out of sync with the deck.
  static targets = ["slide", "stat", "haptic", "cta"]
  static values = {
    // Where the last CTA sends the user. Server-rendered so the native shell
    // gets its recede path and the web gets the dashboard.
    doneUrl: String,
    // Written to localStorage the moment the deck is reached. The dashboard
    // strip reads it to decide whether to show its X: the recap lives for one
    // day, so it must not be dismissible before it has been seen.
    seenKey: String
  }

  connect() {
    this.index = 0
    this._markSeen()
    // A pointer/touch gesture is only a swipe once it has committed to the
    // horizontal axis. Same arrangement budget_carousel uses.
    this.pointer = null
    this.reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false

    this._lockScroll()
    this._show(0, { animate: false })
  }

  disconnect() {
    this._unlockScroll()
    this._stopCount()
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  next() {
    if (this.index >= this.slideTargets.length - 1) {
      this._finish()
      return
    }
    this._tap()
    this._show(this.index + 1)
  }

  previous() {
    if (this.index === 0) return
    this._tap()
    this._show(this.index - 1)
  }

  // Tap zones: left half goes back, right half advances. The zones sit under
  // the CTA, so a tap on the button never double-fires.
  tap(event) {
    const half = this.element.clientWidth / 2
    const x = event.clientX ?? 0
    // Mirrored in RTL: "forward" is whichever side the language reads towards.
    const forward = this._rtl() ? x < half : x >= half
    forward ? this.next() : this.previous()
  }

  // ── Swipe ────────────────────────────────────────────────────────────────

  pointerDown(event) {
    this.pointer = { x: event.clientX, y: event.clientY, axis: null }
  }

  pointerMove(event) {
    if (!this.pointer || this.pointer.axis) return

    const dx = event.clientX - this.pointer.x
    const dy = event.clientY - this.pointer.y
    if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return

    // Commit to one axis for the rest of the gesture. A vertical drag on a
    // full-screen deck is the pull-to-dismiss the native modal owns, so it is
    // left alone rather than swallowed.
    this.pointer.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y"
  }

  pointerUp(event) {
    if (!this.pointer) return

    const dx = event.clientX - this.pointer.x
    const committed = this.pointer.axis === "x"
    this.pointer = null
    if (!committed || Math.abs(dx) < 45) return

    const forward = this._rtl() ? dx > 0 : dx < 0
    forward ? this.next() : this.previous()
  }

  // ── Rendering ────────────────────────────────────────────────────────────

  _show(index, { animate = true } = {}) {
    this.index = index

    this.slideTargets.forEach((slide, i) => {
      const live = i === index
      slide.classList.toggle("is-live", live)
      slide.setAttribute("aria-hidden", live ? "false" : "true")
      // Only the live slide can be reached by the keyboard.
      slide.querySelectorAll("button, a").forEach((el) => {
        el.tabIndex = live ? 0 : -1
      })
    })

    const live = this.slideTargets[index]
    // The host carries the slide's colour, so the page wash, the numeral and
    // the eyebrow chip all recolour together in one transition.
    this.element.style.setProperty("--tint", `var(--color-${live.dataset.tint})`)

    if (animate) this._replay(live)
    this._countUp(live)
  }

  // Restart the slide's CSS entry animations. Removing and re-adding the class
  // is not enough on its own — the browser coalesces both mutations into one
  // frame and nothing replays — so the reflow between them is load bearing.
  _replay(slide) {
    if (this.reduced) return
    slide.classList.remove("is-entering")
    void slide.offsetWidth
    slide.classList.add("is-entering")
  }

  // ── Stat count-up ────────────────────────────────────────────────────────

  // The numeral rolls from zero to its value. Only digits move: the currency
  // symbol, the separators and any suffix are template text the server already
  // formatted, so the roll can never produce a string the server would not.
  _countUp(slide) {
    this._stopCount()

    const el = slide.querySelector("[data-month-recap-target='stat']")
    if (!el) return

    const target = Number(el.dataset.value)
    const template = el.dataset.template || "%d"
    if (!Number.isFinite(target) || target === 0 || this.reduced) return

    const DURATION = 900
    const start = performance.now()

    const step = (now) => {
      // Clamped at BOTH ends. A rAF callback is handed the frame's start time,
      // which can predate the performance.now() taken when the frame was
      // requested — an unclamped negative t runs the easing backwards and
      // flashes a large negative number before the roll begins.
      const t = Math.min(1, Math.max(0, (now - start) / DURATION))
      // Ease-out-expo: fast off the line, settling into the final digits.
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
      const value = Math.round(target * eased)
      el.textContent = template.replace("%d", this._group(value))
      if (t < 1) this.countFrame = requestAnimationFrame(step)
    }

    this.countFrame = requestAnimationFrame(step)
  }

  _stopCount() {
    if (this.countFrame) cancelAnimationFrame(this.countFrame)
    this.countFrame = null
  }

  // Thousands separator for the locale the page is rendered in, so a rolling
  // number never briefly formats differently from the one the server printed.
  _group(value) {
    return value.toLocaleString(document.documentElement.lang || "en")
  }

  // ── Exit ─────────────────────────────────────────────────────────────────

  // Last CTA. The URL is server-rendered: inside the native shell it is the
  // recede path, which dismisses the modal; on the web it is the dashboard.
  _finish() {
    this._tap()
    if (this.hasDoneUrlValue && this.doneUrlValue) window.Turbo.visit(this.doneUrlValue)
  }

  // ── Utilities ────────────────────────────────────────────────────────────

  _rtl() {
    return document.documentElement.dir === "rtl"
  }

  // Records that the deck was opened, which is what arms the dashboard strip's
  // X. Written on connect rather than on finish: reaching the deck at all is
  // what makes the strip dismissible, whether or not every slide was read.
  _markSeen() {
    if (!this.hasSeenKeyValue || !this.seenKeyValue) return
    try {
      window.localStorage.setItem(this.seenKeyValue, "1")
    } catch {
      // Blocked storage just means the strip keeps its X hidden. Harmless.
    }
  }

  _tap() {
    if (!this.hasHapticTarget) return
    this.application
      .getControllerForElementAndIdentifier(this.hapticTarget, "bridge--haptic")
      ?.tap()
  }

  _lockScroll() {
    this.previousOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
  }

  _unlockScroll() {
    document.documentElement.style.overflow = this.previousOverflow || ""
  }
}
